import React, { useCallback, useMemo, useState } from 'react';
import { DeleteGoalsDialog } from 'shared/dialogs/delete-goals';
import { useDeleteGoalsMutation } from 'shared/hooks/queries/use-delete-goals-mutation';
import { ID } from 'shared/types/id';

import {
  DeleteGoalsContext,
  DeleteGoalsContextProps,
} from './delete-goals-context';

export type DeleteGoalsProviderProps = {
  children: React.ReactNode;
};

export const DeleteGoalsProvider: React.FC<DeleteGoalsProviderProps> = ({
  children,
}) => {
  const [ids, setIds] = useState<ID[]>([]);
  const {
    mutate: deleteGoals,
    error,
    isPending: isLoading,
    reset,
  } = useDeleteGoalsMutation();

  const openDialog = useCallback((goalIds: ID[]) => {
    setIds(goalIds);
  }, []);

  const closeDialog = useCallback(() => {
    reset();
    setIds([]);
  }, [reset]);

  const submit = useCallback(() => {
    if (ids.length) {
      deleteGoals(ids, {
        onSuccess: () => {
          closeDialog();
        },
      });
    }
  }, [deleteGoals, ids, closeDialog]);

  const value = useMemo<DeleteGoalsContextProps>(
    () => ({ onDeleteGoals: openDialog }),
    [openDialog],
  );

  return (
    <DeleteGoalsContext.Provider value={value}>
      {children}
      <DeleteGoalsDialog
        open={!!ids.length}
        count={ids.length}
        onClose={closeDialog}
        onSubmit={submit}
        onRetry={submit}
        isLoading={isLoading}
        isError={!!error}
      />
    </DeleteGoalsContext.Provider>
  );
};
